import { Button, ConfigProvider, Flex, Modal, Spin, Typography } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import Filters from "../../components/Filters";
import { useAuth } from "../../context/AuthContext";
import useCameras from "../../hooks/useCameras";
import useCollectionsWithoutRevenue from "../../hooks/useCollectionsWithoutRevenue";
import useDerivedMetricsV2 from "../../hooks/useDerivedMetricsV2";
import useExistsDaysWithoutSchedule from "../../hooks/useExistsDaysWithoutSchedule";
import useExistsZoneWithoutCollection from "../../hooks/useExistsZoneWithoutCollection";
import useHomeConversionV2 from "../../hooks/useHomeConversionV2";
import useHomeRevenueV2 from "../../hooks/useHomeRevenueV2";
import useUnlinkedCollections from "../../hooks/useUnlinkedCollections";
import useZones from "../../hooks/useZones";
import { periodoSemiaberto } from "../general/homeV2Helpers";
import HomeDashboard from "./HomeDashboard";
import Warning from "./Warning";
import { cartoesDaEmAlta } from "./emAltaHelpers";

const { Title, Text, Link } = Typography;

const warnings = [
  {
    about: "câmera",
    link: "/zones_manager",
    title: "Nenhuma câmera cadastrada",
    description:
      "Adicionar uma câmera é o primeiro passo para utilizar a plataforma. Sem o cadastro das câmeras, a utilização da plataforma é inviabilizada.",
  },
  {
    about: "zona",
    link: "/zones_manager",
    title: "Nenhuma zona cadastrada",
    description:
      "Adicione uma zona para utilizar a plataforma. As zonas são necessárias para identificar o fluxo de pessoas em regiões específicas de uma câmera.",
  },
  {
    about: "categoria",
    link: null,
    title: "Zonas sem categorias",
    description:
      "Adicione uma categoria a uma zona para utilizar a plataforma. A relação é necessária para cruzar dados de faturamento com o fluxo de pessoas.",
  },
  {
    about: "horário de funcionamento",
    link: "/opening_hours",
    title: "Horário de funcionamento não cadastrado",
    description:
      "Adicione um horário de funcionamento para utilizar a plataforma. A relação é necessária para cruzar dados de faturamento com o fluxo de pessoas.",
  },
  {
    about: "categoria a uma zona da loja",
    link: null,
    title: "Categorias faturadas não vinculadas a zonas",
    description:
      "Existem categorias sendo faturadas nos últimos 7 dias que não estão vinculadas a nenhuma zona. Vincule-as para melhor análise do fluxo de pessoas.",
  },
  {
    about: "categoria a uma zona da loja",
    link: null,
    title: "Categorias não faturadas vinculadas a zonas",
    description:
      "Existem categorias que não estão sendo faturadas nos últimos 15 dias e ainda estão relacionadas a zonas. Verifique se as categorias estão corretas e se estão sendo faturadas.",
  },
];

const getDefaultHomeRange = () => [
  dayjs().subtract(30, "day").format("YYYY-MM-DD"),
  dayjs().subtract(1, "day").format("YYYY-MM-DD"),
];

export default function Home() {
  const {
    data: cameras,
    camerasCount,
    isLoading: isLoadingCamerasCount,
  } = useCameras();
  const { count: zonesCount, isLoading: isLoadingZonesCount } = useZones({
    active_only: true,
  });

  const {
    data: existsZoneWithoutCollection,
    isLoading: isLoadingExistsZoneWithoutCollection,
  } = useExistsZoneWithoutCollection();
  const {
    data: existsDaysWithoutSchedule,
    isLoading: isLoadingExistsDaysWithoutSchedule,
  } = useExistsDaysWithoutSchedule();

  const {
    user,
    setIsZeroPhaseTutorialOpen,
    shouldShowTutorialBool,
    setShouldShowTutorialBool,
    postShouldNeverShowTutorial,
  } = useAuth();

  const [isModalOpen, setIsModalOpen] = useState(true);
  const [selectedDateRange, setSelectedDateRange] =
    useState(getDefaultHomeRange);

  const handlePreviousPeriod = () => {
    const [start, end] = selectedDateRange;
    const startDay = dayjs(start);
    const endDay = dayjs(end);
    const isFullCalendarYear =
      startDay.month() === 0 &&
      startDay.date() === 1 &&
      endDay.month() === 11 &&
      endDay.date() === 31 &&
      startDay.year() === endDay.year();
    const isFullCalendarMonth =
      !isFullCalendarYear &&
      startDay.date() === 1 &&
      endDay.date() === endDay.daysInMonth() &&
      startDay.month() === endDay.month() &&
      startDay.year() === endDay.year();
    if (isFullCalendarYear) {
      const prev = startDay.subtract(1, "year");
      setSelectedDateRange([
        prev.startOf("year").format("YYYY-MM-DD"),
        prev.endOf("year").format("YYYY-MM-DD"),
      ]);
    } else if (isFullCalendarMonth) {
      const prev = startDay.subtract(1, "month");
      setSelectedDateRange([
        prev.startOf("month").format("YYYY-MM-DD"),
        prev.endOf("month").format("YYYY-MM-DD"),
      ]);
    } else {
      const duration = endDay.diff(startDay, "day") + 1;
      setSelectedDateRange([
        startDay.subtract(duration, "day").format("YYYY-MM-DD"),
        endDay.subtract(duration, "day").format("YYYY-MM-DD"),
      ]);
    }
  };

  const handleNextPeriod = () => {
    const [start, end] = selectedDateRange;
    const startDay = dayjs(start);
    const endDay = dayjs(end);
    const isFullCalendarYear =
      startDay.month() === 0 &&
      startDay.date() === 1 &&
      endDay.month() === 11 &&
      endDay.date() === 31 &&
      startDay.year() === endDay.year();
    const isFullCalendarMonth =
      !isFullCalendarYear &&
      startDay.date() === 1 &&
      endDay.date() === endDay.daysInMonth() &&
      startDay.month() === endDay.month() &&
      startDay.year() === endDay.year();
    if (isFullCalendarYear) {
      const next = startDay.add(1, "year");
      setSelectedDateRange([
        next.startOf("year").format("YYYY-MM-DD"),
        next.endOf("year").format("YYYY-MM-DD"),
      ]);
    } else if (isFullCalendarMonth) {
      const next = startDay.add(1, "month");
      setSelectedDateRange([
        next.startOf("month").format("YYYY-MM-DD"),
        next.endOf("month").format("YYYY-MM-DD"),
      ]);
    } else {
      const duration = endDay.diff(startDay, "day") + 1;
      setSelectedDateRange([
        startDay.add(duration, "day").format("YYYY-MM-DD"),
        endDay.add(duration, "day").format("YYYY-MM-DD"),
      ]);
    }
  };

  const hasCameras = camerasCount && camerasCount > 0;
  const hasZones = hasCameras && zonesCount && zonesCount > 0;

  const { data: unlinkedCollections, isLoading: isLoadingUnlinkedCollections } =
    useUnlinkedCollections(Boolean(hasZones && hasCameras));

  const {
    data: collectionsWithoutRevenue,
    isLoading: isLoadingCollectionsWithoutRevenue,
  } = useCollectionsWithoutRevenue(Boolean(hasZones && hasCameras));

  const isLoading =
    isLoadingCamerasCount ||
    isLoadingZonesCount ||
    isLoadingExistsZoneWithoutCollection ||
    isLoadingExistsDaysWithoutSchedule ||
    isLoadingUnlinkedCollections ||
    isLoadingCollectionsWithoutRevenue;

  const branchIds = useMemo(
    () => (user?.active_branch_id ? [user.active_branch_id] : []),
    [user?.active_branch_id]
  );
  const periodo = useMemo(
    () => periodoSemiaberto(selectedDateRange),
    [selectedDateRange]
  );

  const faturamento = useHomeRevenueV2({
    branchIds,
    ...periodo,
    tipo: "presencial",
    compare: "previous",
  });
  const conversao = useHomeConversionV2({
    branchIds,
    ...periodo,
    compare: "previous",
  });
  const derivadas = useDerivedMetricsV2({
    branchIds,
    ...periodo,
    tipo: "presencial",
    compare: "previous",
  });

  const isLoadingCartoes = [faturamento, conversao, derivadas].some(
    (consulta) => consulta.isLoading
  );
  const cartoes = useMemo(
    () =>
      cartoesDaEmAlta({
        atual: {
          revenue: faturamento.data,
          conversion: conversao.data,
          derived: derivadas.data,
        },
        anterior: {
          revenue: faturamento.data?.compare,
          conversion: conversao.data?.compare,
          derived: derivadas.data?.compare,
        },
      }),
    [faturamento.data, conversao.data, derivadas.data]
  );

  const handleCloseModal = async () => {
    await postShouldNeverShowTutorial();
    localStorage.setItem("should_never_show_tutorial", true);
    setShouldShowTutorialBool(false);
  };

  return (
    <ConfigProvider
      theme={{
        components: { Card: { headerBg: "var(--ouran-card-header-bg)" } },
      }}>
      <Modal
        open={isModalOpen && shouldShowTutorialBool}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => {
          setIsModalOpen(false);
          setIsZeroPhaseTutorialOpen(true);
        }}
        okText="Iniciar tutorial"
        cancelText="Pular tutorial"
        cancelButtonProps={{ danger: true, onClick: handleCloseModal }}>
        <Title level={1}>Bem-vindo!</Title>
        <Text>
          {
            "Essa é a plataforma de monitoramento de fluxo de pessoas chamada Ouran! Para iniciar, adicione "
          }
          <Link href="/zones_manager">câmeras</Link>
          {". Depois, relacione câmeras a zonas para análise detalhada."}
        </Text>
      </Modal>

      <Flex
        style={{ width: "100%", minHeight: "100vh", gap: 16 }}
        justify="flex-start"
        align="stretch"
        vertical>
        {isLoading ? (
          <Spin size="large" style={{ margin: "auto" }} />
        ) : (
          <>
            {hasZones && hasCameras && !existsDaysWithoutSchedule && (
              <Flex style={{ width: "100%", padding: "0 2rem" }}>
                <Filters
                  page="home"
                  dateRange={true}
                  compact={true}
                  title={null}
                  cardStyle={{ width: "100%" }}
                  selectedDateRange={selectedDateRange}
                  setSelectedDateRange={setSelectedDateRange}
                  filters={["data"]}
                  isMainDataLoading={isLoadingCartoes}
                  dateAddon={
                    <Flex gap={8}>
                      <Button onClick={handlePreviousPeriod}>
                        &#8592; Período Anterior
                      </Button>
                      <Button onClick={handleNextPeriod}>
                        Período Posterior &#8594;
                      </Button>
                    </Flex>
                  }
                />
              </Flex>
            )}

            {hasZones && hasCameras && !existsDaysWithoutSchedule && (
              <HomeDashboard
                cartoes={cartoes}
                isLoadingCartoes={isLoadingCartoes}
                branchIds={branchIds}
                periodo={periodo}
              />
            )}

            <Flex
              justify="flex-start"
              align="stretch"
              vertical
              gap="4px"
              style={{
                width: "100%",
                padding: "0 2rem",
                paddingBottom: "24px",
              }}>
              {!hasCameras && (
                <Warning
                  title={warnings[0].title}
                  description={warnings[0].description}
                  about={warnings[0].about}
                  link={warnings[0].link}
                />
              )}
              {!hasZones && (
                <Warning
                  title={warnings[1].title}
                  description={warnings[1].description}
                  about={warnings[1].about}
                  link={warnings[1].link}
                />
              )}
              {existsZoneWithoutCollection && (
                <Warning
                  title={warnings[2].title}
                  description={warnings[2].description}
                  about={warnings[2].about}
                  link={warnings[2].link}
                />
              )}
              {existsDaysWithoutSchedule && (
                <Warning
                  title={warnings[3].title}
                  description={warnings[3].description}
                  about={warnings[3].about}
                  link={warnings[3].link}
                />
              )}
              {unlinkedCollections?.total_nao_vinculadas > 0 && (
                <Warning
                  title={warnings[4].title}
                  description={warnings[4].description}
                  about={warnings[4].about}
                  link={warnings[4].link}
                  colecoes={unlinkedCollections.colecoes_nao_vinculadas}
                />
              )}
              {collectionsWithoutRevenue?.total_sem_faturamento > 0 && (
                <Warning
                  title={warnings[5].title}
                  description={warnings[5].description}
                  about={warnings[5].about}
                  link={warnings[5].link}
                  colecoes={collectionsWithoutRevenue.colecoes_sem_faturamento}
                />
              )}
            </Flex>
          </>
        )}
      </Flex>
    </ConfigProvider>
  );
}
